import type {
  OptionListProps,
  OptionValue,
  SearchableListGroupBy,
  SearchableListOption,
  SearchableListProps,
  SearchableListSection,
} from './types'

interface SearchableListGrouping {
  sort: (options: SearchableListOption[], locale?: string) => SearchableListOption[]
  group: (options: SearchableListOption[], locale?: string) => SearchableListSection[]
}

const NON_LETTER_GROUP = '#'

const filterOptionsByQuery = (options: SearchableListOption[], query: string) => {
  if (!query.trim()) {
    return options
  }
  return options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
}

const getGroupLetter = (label: string, locale?: string) => {
  const first = label.trim().normalize('NFD').charAt(0)
  const isLetter = first !== '' && first.toLowerCase() !== first.toUpperCase()
  return isLetter ? first.toLocaleUpperCase(locale) : NON_LETTER_GROUP
}

const groupAlphabetically = (
  options: SearchableListOption[],
  locale?: string
): SearchableListSection[] => {
  const buckets = new Map<string, SearchableListOption[]>()
  for (const option of options) {
    const letter = getGroupLetter(option.label, locale)
    buckets.set(letter, [...(buckets.get(letter) ?? []), option])
  }
  const entries = [...buckets]
  const letterEntries = entries.filter(([letter]) => letter !== NON_LETTER_GROUP)
  const nonLetterEntries = entries.filter(([letter]) => letter === NON_LETTER_GROUP)
  return [...letterEntries, ...nonLetterEntries].map(([letter, letterOptions]) => ({
    kind: 'group',
    key: `group-${letter}`,
    title: letter,
    options: letterOptions,
  }))
}

const getPopularOptions = (options: SearchableListOption[], popularValues: OptionValue[]) =>
  popularValues
    .map((value) => options.find((option) => option.value === value))
    .filter((option): option is SearchableListOption => option !== undefined)

const sortAlphabetically = (options: SearchableListOption[], locale?: string) => {
  const collator = new Intl.Collator(locale, { sensitivity: 'base' })
  return [...options].sort((a, b) => collator.compare(a.label, b.label))
}

const GROUPINGS: Record<SearchableListGroupBy, SearchableListGrouping> = {
  alphabetical: { sort: sortAlphabetically, group: groupAlphabetically },
}

const hasSections = (
  searchQuery: string,
  leading: SearchableListOption[],
  grouping?: SearchableListGrouping
) => !searchQuery.trim() && (leading.length > 0 || grouping !== undefined)

const toLeadingSection = (
  kind: 'pinned' | 'popular',
  options: SearchableListOption[]
): SearchableListSection[] => (options.length ? [{ kind, key: kind, options }] : [])

const getRemainingSections = (
  remaining: SearchableListOption[],
  grouping?: SearchableListGrouping,
  locale?: string
): SearchableListSection[] =>
  grouping ? grouping.group(remaining, locale) : [{ kind: 'all', key: 'all', options: remaining }]

interface BuildSectionsParams {
  options: SearchableListOption[]
  popularOptions?: OptionValue[]
  pinnedOptions?: readonly OptionValue[]
  groupBy?: SearchableListGroupBy
  locale?: string
  searchQuery: string
}

export const buildSections = ({
  popularOptions = [],
  pinnedOptions = [],
  ...params
}: BuildSectionsParams) => {
  const grouping = params.groupBy ? GROUPINGS[params.groupBy] : undefined
  const matching = filterOptionsByQuery(params.options, params.searchQuery)
  const filteredOptions = grouping ? grouping.sort(matching, params.locale) : matching
  const pinned = getPopularOptions(filteredOptions, [...pinnedOptions])
  const popular = getPopularOptions(filteredOptions, popularOptions)

  if (!hasSections(params.searchQuery, [...pinned, ...popular], grouping)) {
    return { filteredOptions, sections: null }
  }

  const leadingValues = [...pinnedOptions, ...popularOptions]
  const remaining = filteredOptions.filter((option) => !leadingValues.includes(option.value))
  const sections = [
    ...toLeadingSection('pinned', pinned),
    ...toLeadingSection('popular', popular),
    ...getRemainingSections(remaining, grouping, params.locale),
  ]
  return { filteredOptions, sections }
}

export const getEmptyResultOptions = (
  options: SearchableListOption[],
  filteredOptions: SearchableListOption[],
  pinnedOptions: readonly OptionValue[] = []
) => (filteredOptions.length ? [] : getPopularOptions(options, [...pinnedOptions]))

export const getSectionTitle = (
  section: SearchableListSection,
  labels: SearchableListProps['labels']
) => {
  if (section.kind === 'pinned') {
    return undefined
  }
  return section.kind === 'group' ? section.title : labels[section.kind]
}

interface SectionListSection {
  key: string
  title?: string
  data: SearchableListOption[]
}

export const toSectionListData = ({
  sections,
  filteredOptions,
  labels,
}: Pick<OptionListProps, 'sections' | 'filteredOptions' | 'labels'>): SectionListSection[] => {
  if (!filteredOptions.length) {
    return []
  }
  if (!sections) {
    return [{ key: 'results', data: filteredOptions }]
  }
  return sections.map((section) => ({
    key: section.key,
    title: getSectionTitle(section, labels),
    data: section.options,
  }))
}

export const getSelectedValues = (value: OptionValue | OptionValue[] | undefined | null) => {
  if (Array.isArray(value)) {
    return value
  }
  return value === undefined || value === null ? [] : [value]
}

const hasReachedMax = (selectedValues: OptionValue[], maxSelections?: number) =>
  maxSelections !== undefined && selectedValues.length >= maxSelections

export const removeSelectionAt = (selected: OptionValue[], index: number) =>
  selected.filter((_, selectedIndex) => selectedIndex !== index)

export const toggleSelection = (
  value: OptionValue,
  selected: OptionValue[],
  exclusiveValues: readonly OptionValue[]
) => {
  if (selected.includes(value)) {
    return removeSelectionAt(selected, selected.lastIndexOf(value))
  }
  if (exclusiveValues.includes(value)) {
    return [value]
  }
  return [...selected, value]
}

interface SelectionState {
  selectedValues: OptionValue[]
  exclusiveValues: readonly OptionValue[]
  repeatableValues: readonly OptionValue[]
  isLocked: boolean
}

export const getSelectionState = (props: SearchableListProps): SelectionState => {
  const listedValues = new Set(props.options.map((option) => option.value))
  const selectedValues = getSelectedValues(props.value).filter((value) => listedValues.has(value))
  const exclusiveValues = props.exclusiveValues ?? []
  const hasExclusiveSelected = selectedValues.some((value) => exclusiveValues.includes(value))
  const isAtMax = hasReachedMax(selectedValues, props.maxSelections)
  return {
    selectedValues,
    exclusiveValues,
    repeatableValues: props.repeatableValues ?? [],
    isLocked: props.selectionMode === 'multiple' && (hasExclusiveSelected || isAtMax),
  }
}

/** Adds another pick of a repeatable value while there is room, otherwise toggles the value. */
export const pickSelection = (value: OptionValue, selection: SelectionState) =>
  !selection.isLocked && selection.repeatableValues.includes(value)
    ? [...selection.selectedValues, value]
    : toggleSelection(value, selection.selectedValues, selection.exclusiveValues)

export const isBlockedOption = (value: OptionValue, selection: SelectionState) =>
  selection.isLocked &&
  !selection.selectedValues.includes(value) &&
  !selection.exclusiveValues.includes(value)

/** One entry per allowed pick, in pick order; `undefined` marks a free slot. */
export const toSelectionSlots = (
  options: SearchableListOption[],
  selectedValues: OptionValue[],
  maxSelections: number
) => {
  const selected = selectedValues.flatMap((value) =>
    options.filter((option) => option.value === value)
  )
  return Array.from({ length: maxSelections }, (_, index) => selected.at(index))
}
